import React from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import { allowDecimalByCountry } from 'user/selectors';
import { connect } from 'react-redux';

// constants
import { applicationProperties } from 'constants/scripts';

// components
import { Dropdown } from '@tcc/crate';
import ColorBullet from 'common/ColorBullet';
import ZoneMeta from 'edit/ZoneMeta';
import ZoneProductApplicationInputWeb from 'edit/ZoneProductApplicationInputWeb';

// util
import { formatInputText } from 'util/formatters/textInputFormatter';
import { decimalPoint } from 'util/formatters/numberFormatter';
import { getApplicationProperty } from 'util/applications';
import { getPlantingRates } from 'util/planting';

const baseString = 'vra-zone-container';

export class PlantingZone extends React.Component {
  constructor(props) {
    super(props);

    const {
      allowDecimalByCountry,
      cropNominalWeight,
      fertilizerApplications,
      seedApplications
    } = props;

    const { seedTargetYield } = getPlantingRates(
      fertilizerApplications,
      seedApplications,
      cropNominalWeight
    );

    this.state = {
      seedTargetYieldValue:
        seedTargetYield && allowDecimalByCountry
          ? decimalPoint(seedTargetYield.toFixed(1))
          : Math.round(seedTargetYield)
    };
  }

  onSeedTargetYieldChange = (parsedValue) => {
    const { allowDecimalByCountry } = this.props;
    this.setState({ seedTargetYieldValue: parsedValue.value });
    this.props.onChangeProductApplicationInput({
      ...parsedValue,
      value: allowDecimalByCountry
        ? parsedValue.value.replace(',', '.')
        : parsedValue.value
    });
  };

  render = () => {
    const {
      activeSeedProducts,
      canChooseSeedProduct,
      cropNominalWeight,
      fertilizerApplications,
      isCheckStrip,
      isEditingDisabled,
      isSelected,
      onBlurProductApplicationInput,
      onChangeProductApplicationInput,
      onClickProductApplicationInput,
      onClickZone,
      onSelectSeedProduct,
      seedApplications,
      showLiquidApplication,
      validationResult,
      zoneArea,
      zoneDisplayNumber,
      zoneId,
      zoneIdentifierColor,
      zoneRef,
      allowDecimalByCountry,
      zoneProductivity,
      isDeadZone
    } = this.props;

    const classString = classNames({
      [`${baseString}`]: true,
      'vra-planting-zone-container': true,
      isSelected: isSelected
    });

    let numCols = 2;
    if (showLiquidApplication) {
      numCols += 1;
    }

    const { liquidRate, seedRate } = getPlantingRates(
      fertilizerApplications,
      seedApplications,
      cropNominalWeight
    );

    const liquidRateValidationResult = get(
      validationResult,
      applicationProperties.FERTILIZER_RATE
    );
    const seedRateValidationResult = get(
      validationResult,
      applicationProperties.SEED_RATE
    );
    const seedTargetYieldValidationResult = get(
      validationResult,
      applicationProperties.TARGET_YIELD
    );

    let selectedSeedProductName, seedProductOptions;
    if (canChooseSeedProduct) {
      selectedSeedProductName = getApplicationProperty(
        seedApplications,
        applicationProperties.PRODUCT,
        'name'
      );
      seedProductOptions = activeSeedProducts.map(({ id, name, brand }) => {
        return { id, value: name, description: brand };
      });
      numCols += 1;
    }

    return (
      <tbody
        className={classString}
        data-test-id={'planting-zone-container'}
        onClick={() => onClickZone(zoneId)}
        ref={(e) => zoneRef(e, zoneId)}
      >
        <tr className={`${baseString}__zone-properties`}>
          {/* colored dot */}
          <td className={`${baseString}__zone-identifier`}>
            <ColorBullet color={zoneIdentifierColor} />
          </td>
          {/* zone name, acreage and soil data */}
          <td className={`${baseString}__meta`}>
            <ZoneMeta
              isCheckStrip={isCheckStrip}
              zoneArea={zoneArea}
              zoneDisplayNumber={zoneDisplayNumber}
              zoneId={zoneId}
              zoneProductivity={zoneProductivity}
              isDeadZone={isDeadZone}
            />
          </td>
          {/* seed target yield */}
          <td
            className={`${baseString}__application-property--${applicationProperties.TARGET_YIELD}`}
          >
            <ZoneProductApplicationInputWeb
              allowOneDecimal={allowDecimalByCountry}
              isEditingDisabled={isEditingDisabled}
              onBlur={onBlurProductApplicationInput}
              onChange={this.onSeedTargetYieldChange}
              onClick={onClickProductApplicationInput}
              property={applicationProperties.TARGET_YIELD}
              validationResult={seedTargetYieldValidationResult}
              value={this.state.seedTargetYieldValue}
            />
          </td>
          {/* seed rate */}
          <td
            className={`${baseString}__application-property--${applicationProperties.SEED_RATE}`}
          >
            <ZoneProductApplicationInputWeb
              isEditingDisabled={isEditingDisabled}
              onBlur={onBlurProductApplicationInput}
              onChange={onChangeProductApplicationInput}
              onClick={onClickProductApplicationInput}
              property={applicationProperties.SEED_RATE}
              validationResult={seedRateValidationResult}
              value={formatInputText(seedRate)}
            />
          </td>
          {/* seed product */}
          {canChooseSeedProduct && (
            <td className={`${baseString}__application-property--seed-product`}>
              <Dropdown
                disabled={isEditingDisabled}
                options={seedProductOptions}
                setValue={(name) => {
                  const selectedSeedProduct = activeSeedProducts.find(
                    (seed) => get(seed, 'name') === name
                  );
                  onSelectSeedProduct({ productId: selectedSeedProduct.id });
                }}
                value={selectedSeedProductName}
              />
            </td>
          )}
          {/* liquid product */}
          {showLiquidApplication && (
            <td
              className={`${baseString}__application-property--${applicationProperties.FERTILIZER_RATE}`}
            >
              <ZoneProductApplicationInputWeb
                isEditingDisabled={isEditingDisabled}
                onBlur={onBlurProductApplicationInput}
                onChange={onChangeProductApplicationInput}
                onClick={onClickProductApplicationInput}
                property={applicationProperties.FERTILIZER_RATE}
                validationResult={liquidRateValidationResult}
                value={formatInputText(liquidRate)}
              />
            </td>
          )}
        </tr>
        <tr className={`${baseString}__help-messages`}>
          <td colSpan="2" />
          <td colSpan={numCols} />
        </tr>
      </tbody>
    );
  };
}

PlantingZone.propTypes = {
  activeSeedProducts: propTypes.array.isRequired,
  allowDecimalByCountry: propTypes.bool,
  canChooseSeedProduct: propTypes.bool.isRequired,
  cropNominalWeight: propTypes.number.isRequired,
  fertilizerApplications: propTypes.array.isRequired,
  isCheckStrip: propTypes.bool,
  isEditingDisabled: propTypes.bool.isRequired,
  isSelected: propTypes.bool.isRequired,
  onBlurProductApplicationInput: propTypes.func.isRequired,
  onChangeProductApplicationInput: propTypes.func.isRequired,
  onClickProductApplicationInput: propTypes.func.isRequired,
  onClickZone: propTypes.func,
  onSelectSeedProduct: propTypes.func.isRequired,
  seedApplications: propTypes.array.isRequired,
  showLiquidApplication: propTypes.bool.isRequired,
  validationResult: propTypes.object,
  zoneArea: propTypes.object.isRequired,
  zoneDisplayNumber: propTypes.number.isRequired,
  zoneId: propTypes.string.isRequired,
  zoneIdentifierColor: propTypes.object.isRequired,
  zoneRef: propTypes.func.isRequired,
  zoneProductivity: propTypes.number,
  isDeadZone: propTypes.bool
};

export default connect((state) => ({
  allowDecimalByCountry: allowDecimalByCountry(state)
}))(PlantingZone);
